import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useUserAdsStore } from '../../stores/useDataStore';
import { useAdminStore } from '../../stores/adminStore'; // 🟢 1. Importamos el adminStore
import { useAuthStore } from '../../stores/useAuthStore'; // 1. Importamos el authStore para obtener el usuario
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import AdsFinanceEngine from './AdsFinanceEngine';

const COUNTRY_CODES = [
  { code: '+1', label: 'US/CA (+1)' },
  { code: '+34', label: 'ES (+34)' },
  { code: '+52', label: 'MX (+52)' },
  { code: '+54', label: 'AR (+54)' },
  { code: '+57', label: 'CO (+57)' },
  { code: '+58', label: 'VE (+58)' },
  { code: '+51', label: 'PE (+51)' },
  { code: '+56', label: 'CL (+56)' },
];

const toE164Phone = (countryCode, localPhone) => {
  const prefix = String(countryCode || '').replace(/[^+\d]/g, '');
  const local = String(localPhone || '').replace(/\D/g, '');
  const composed = `${prefix}${local}`;
  return /^\+[1-9]\d{7,14}$/.test(composed) ? composed : null;
};

const CreateAdForm = ({ onFormSubmit, adToEdit, isAdminMode = false, campaignId, modalData }) => { // 🟢 FASE 4: formulario unificado
  const resolvedAdToEdit = adToEdit || modalData?.adToEdit;
  const resolvedCampaignId = campaignId || modalData?.campaignId;
  const isEditMode = !!resolvedAdToEdit;
  const [rateKey, setRateKey] = useState('STANDARD');
  const [financeApproved, setFinanceApproved] = useState(false);
  const [financeError, setFinanceError] = useState('');
  const { register, handleSubmit, watch, formState: { errors, isSubmitting }, reset } = useForm({
    defaultValues: {
      actionType: 'url',
      countryCode: '+1',
    },
  });
  
  // 🟢 3. Obtenemos las acciones de ambos stores
  const userCreateAd = useUserAdsStore((state) => state.createAd);
  const userUpdateAd = useUserAdsStore((state) => state.updateAd);
  const adminCreateAd = useAdminStore((state) => state.createAd);

  // Determinamos qué función de creación/actualización usar
  const createAd = isAdminMode ? adminCreateAd : userCreateAd;
  const updateAd = userUpdateAd; // La edición por ahora solo la hacen los usuarios

  const currentUser = useAuthStore((state) => state.currentUser); // 2. Obtenemos el usuario actual
  const isRoot = currentUser?.roles?.includes('admin_root') || currentUser?.role === 'admin_root';

  // 3. Definimos la lógica de permisos en el frontend
  const canCreateExternalAds = currentUser?.role === 'admin' || currentUser?.companies?.length > 0 || currentUser?.stores?.length > 0;
  const selectedActionType = watch('actionType') || 'url';
  const startDateValue = watch('startDate');
  const endDateValue = watch('endDate');

  useEffect(() => {
    if (isEditMode && resolvedAdToEdit) {
      // Rellenamos el formulario con los datos del anuncio a editar
      reset({
        ...resolvedAdToEdit,
        startDate: resolvedAdToEdit.startDate ? format(new Date(resolvedAdToEdit.startDate), 'yyyy-MM-dd') : '',
        endDate: resolvedAdToEdit.endDate ? format(new Date(resolvedAdToEdit.endDate), 'yyyy-MM-dd') : '',
        placement: resolvedAdToEdit.placement?.[0] || '', // Asumimos un solo placement por ahora
        actionType: resolvedAdToEdit.actionType || 'url',
        phone: resolvedAdToEdit.phone || resolvedAdToEdit.whatsappNumber || resolvedAdToEdit.contactPhone || '',
        countryCode: COUNTRY_CODES.find((c) => (resolvedAdToEdit.phone || '').startsWith(c.code))?.code || '+1',
        internalPath: resolvedAdToEdit.internalPath || resolvedAdToEdit.actionValue || '',
      });
    } else {
      reset(); // Limpiamos el formulario si no estamos en modo edición
    }
  }, [resolvedAdToEdit, isEditMode, reset]);

  useEffect(() => {
    setFinanceApproved(false);
    setFinanceError('');
  }, [startDateValue, endDateValue, rateKey]);

  const onSubmit = async (data) => {
    try {
      if (!financeApproved) {
        setFinanceError('Confirma el presupuesto antes de crear la campana.');
        return;
      }
      // Convertimos las fechas a formato ISO
      const adData = {
        ...data,
        startDate: new Date(data.startDate).toISOString(),
        endDate: data.endDate
          ? new Date(data.endDate).toISOString()
          : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        placement: [data.placement], // El backend espera un array
        campaign: resolvedCampaignId,
        rateKey,
        isSovereign: isRoot,
      };

      if (adData.actionType === 'whatsapp') {
        const e164Phone = toE164Phone(adData.countryCode, adData.phone);
        if (!e164Phone) {
          throw new Error('Número de WhatsApp inválido. Usa un prefijo internacional válido.');
        }
        adData.phone = e164Phone;
      }

      if (adData.actionType !== 'url') {
        delete adData.linkUrl;
      }

      if (adData.actionType !== 'internal') {
        delete adData.internalPath;
      }

      if (adData.actionType !== 'whatsapp') {
        delete adData.phone;
      }

      delete adData.countryCode;

      if (isEditMode) {
        await updateAd(resolvedAdToEdit._id, adData);
      } else {
        await createAd(adData);
      }
      if (typeof onFormSubmit === 'function') {
        onFormSubmit(); // Cerramos el formulario si el envío es exitoso
      }
    } catch (error) {
      // El toast de error ya se muestra en el store
      console.error("Fallo al crear el anuncio desde el formulario:", error);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6"> 
        {isEditMode ? 'Editar Anuncio' : 'Crear Nuevo Anuncio'}
      </h2>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Título del Anuncio</label>
          <input type="text" id="title" {...register('title', { required: 'El título es obligatorio' })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
          {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title.message}</p>}
        </div>

        <div>
          <label htmlFor="content" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Contenido / Descripción</label>
          <textarea id="content" {...register('content')} rows="3" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"></textarea>
        </div>

        {/* 4. Renderizado condicional del campo de URL externa */}
        <div>
          <label htmlFor="actionType" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Tipo de Acción</label>
          <select
            id="actionType"
            {...register('actionType', { required: 'Debes seleccionar el tipo de acción' })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="url">URL Externa</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="internal">Ruta Interna</option>
          </select>
        </div>

        {(isAdminMode || canCreateExternalAds) ? (
          <>
            {selectedActionType === 'url' && (
              <div>
                <label htmlFor="linkUrl" className="block text-sm font-medium text-gray-700 dark:text-gray-300">URL de Destino (ej: https://misitio.com)</label>
                <input type="url" id="linkUrl" {...register('linkUrl', { required: 'La URL de destino es obligatoria para este tipo de acción.' })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                {errors.linkUrl && <p className="text-red-500 text-xs mt-1">{errors.linkUrl.message}</p>}
              </div>
            )}

            {selectedActionType === 'whatsapp' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label htmlFor="countryCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300">País</label>
                  <select
                    id="countryCode"
                    {...register('countryCode', { required: 'Selecciona prefijo internacional' })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    {COUNTRY_CODES.map((country) => (
                      <option key={country.code} value={country.code}>{country.label}</option>
                    ))}
                  </select>
                  {errors.countryCode && <p className="text-red-500 text-xs mt-1">{errors.countryCode.message}</p>}
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Teléfono WhatsApp</label>
                  <input
                    type="text"
                    id="phone"
                    placeholder="Ej: 4121234567"
                    {...register('phone', {
                      required: 'El teléfono es obligatorio para WhatsApp.',
                      validate: (value) => !!toE164Phone(watch('countryCode'), value) || 'Ingresa un número válido con prefijo internacional.',
                    })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
                </div>
              </div>
            )}

            {selectedActionType === 'internal' && (
              <div>
                <label htmlFor="internalPath" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Ruta Interna (ej: /ads-center)</label>
                <input type="text" id="internalPath" {...register('internalPath', { required: 'La ruta interna es obligatoria para este tipo de acción.' })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                {errors.internalPath && <p className="text-red-500 text-xs mt-1">{errors.internalPath.message}</p>}
              </div>
            )}
          </>
        ) : (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-center">
            <p className="text-sm text-blue-800">
              Para crear anuncios con enlaces a sitios web externos, necesitas una Página de Empresa o Tienda.
            </p>
            <Link to="/pages/create" className="text-sm font-bold text-blue-600 hover:underline mt-2 inline-block">
              ¡Crea tu página ahora!
            </Link>
          </div>
        )}

        <div>
          <label htmlFor="placement" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Ubicación del Anuncio</label>
          <select id="placement" {...register('placement', { required: 'Debes elegir una ubicación' })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
            <option value="">Selecciona una ubicación...</option>
            <option value="top_nav_hot_news">Top Nav Hot News (Solo texto)</option>
            <option value="featured_video_hibrid">Featured Video Hibrid (Video/GIF/Imagen)</option>
            <option value="world_news_feed">World News Feed (Inyeccion global)</option>
            <option value="stories_feed">Stories Feed (Historias en Feed)</option>
            <option value="home_top_grid">Home Top Grid (Bloques destacados)</option>
          </select>
          {errors.placement && <p className="text-red-500 text-xs mt-1">{errors.placement.message}</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Fecha de Inicio</label>
            <input type="date" id="startDate" {...register('startDate', { required: 'La fecha de inicio es obligatoria' })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            {errors.startDate && <p className="text-red-500 text-xs mt-1">{errors.startDate.message}</p>}
          </div>
          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Fecha de Fin (Opcional)</label>
            <input type="date" id="endDate" {...register('endDate')} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
          </div>
        </div>

        {!isEditMode && (
          <AdsFinanceEngine
            startDate={startDateValue}
            endDate={endDateValue}
            initialRateKey={rateKey}
            onRateChange={setRateKey}
            onConfirm={(snapshot) => {
              setFinanceApproved(true);
              setFinanceError('');
            }}
          />
        )}

        {financeError && (
          <div className="text-xs text-rose-600">{financeError}</div>
        )}

        <div className="flex justify-end">
          <button type="submit" disabled={isSubmitting || (!isEditMode && !financeApproved)} className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-indigo-300">
            {isSubmitting ? (isEditMode ? 'Guardando...' : 'Creando...') : (isEditMode ? 'Guardar Cambios' : 'Crear Anuncio')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateAdForm;